import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Spinner } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import { PATH } from '@/router/PATH';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ManualImportFormState } from './manualImportMeta';
import { ManualImportView } from './components';
import { useManualImport } from './useManualImport';
import { useManualImportDraft } from './useManualImportDraft';

/**
 * /purchases/manual-import (+ /:draftId) — enter a purchase by hand: one
 * supplier → one branch, product lines (restock existing or define new),
 * extra expenses, and a live cost/markup summary, with Save Draft / Discard /
 * Import.
 *
 * Save Draft persists to `/purchases/draft-invoices`; opening the page with a
 * draft id (route param from the drafts table, or the `?draftId=` the first
 * save wrote) hydrates the saved draft first. Like the create-product page
 * there's no `ErrorBoundary` — the picker/option queries degrade to empty
 * lists and problems surface inline.
 */
export const ManualImport: React.FC = () => {
    const draft = useManualImportDraft();
    const { t } = useTranslation('pages');

    if (draft.hydrateError) {
        return (
            <PageShell>
                <Widget
                    className="flex flex-col items-start gap-3 p-6"
                    {...getNodeTestIdProp('manual-import-load-error')}
                >
                    <h1 className="text-lg font-semibold text-foreground">
                        {t('purchases.manualImport.loadError.title')}
                    </h1>
                    <p className="text-sm text-muted">
                        {t('purchases.manualImport.loadError.message')}
                    </p>
                    <div className="flex items-center gap-3">
                        <Button
                            onPress={draft.onRetry}
                            variant="primary"
                            {...getNodeTestIdProp('manual-import-load-retry')}
                        >
                            {t('purchases.manualImport.loadError.retry')}
                        </Button>
                        <Link
                            className="text-sm text-muted hover:text-foreground"
                            to={PATH.PURCHASES.ROOT}
                        >
                            {t('purchases.manualImport.back')}
                        </Link>
                    </div>
                </Widget>
            </PageShell>
        );
    }

    if (draft.isHydrating) {
        return (
            <PageShell>
                <div
                    className="flex justify-center py-24"
                    {...getNodeTestIdProp('manual-import-loading')}
                >
                    <Spinner aria-label={t('purchases.manualImport.loading')} />
                </div>
            </PageShell>
        );
    }

    return (
        <PageShell>
            <ManualImportLoaded
                draftId={draft.draftId}
                initialDraft={draft.initialDraft}
                onDraftCreated={draft.onDraftCreated}
                onDraftDiscarded={draft.onDraftDiscarded}
                onImported={draft.onImported}
            />
        </PageShell>
    );
};

type ManualImportLoadedProps = {
    draftId: string | null;
    initialDraft?: ManualImportFormState;
    onDraftCreated: (draftId: string) => void;
    onDraftDiscarded: () => void;
    onImported: () => void;
};

/** Mounted only once any saved draft is hydrated, so `useManualImport`'s
 *  state initializer sees the seed on first render. */
const ManualImportLoaded: React.FC<ManualImportLoadedProps> = ({
    draftId,
    initialDraft,
    onDraftCreated,
    onDraftDiscarded,
    onImported,
}) => {
    const form = useManualImport(initialDraft, {
        draftId,
        onDraftCreated,
        onDraftDiscarded,
        onImported,
    });

    return <ManualImportView form={form} />;
};

const PageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div
        className="mx-auto flex w-full max-w-7xl flex-col gap-8"
        {...getNodeTestIdProp('manual-import-page')}
        id="manual-import-page"
    >
        {children}
    </div>
);
